import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { Catalogue } from "../../../types/catalogue.types";
import { loadCatalogueCommerceRollup } from "../catalogueShopperSignals";
import {
  activityRangeStart,
  expiredOrDisabledLinkCount,
  inActivityRange,
  inquiryOccurredAt,
  isInviteeView,
  submittedSelectionCount,
  uniqueBuyersInRange,
} from "../customerActivityFeed";

interface PerformanceTabProps {
  catalogue: Catalogue;
  focusLinkToken?: string;
}

function submittedEngagementScore(sig: {
  cartUnits: number;
  shortlistLines: number;
  cartSubmissions: number;
  shortlistSubmissions: number;
}): number {
  return sig.cartUnits + sig.shortlistLines + sig.cartSubmissions * 3 + sig.shortlistSubmissions * 2;
}

const PerformanceTab: React.FC<PerformanceTabProps> = ({ catalogue, focusLinkToken }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState("30d");
  const [uniqueBuyers, setUniqueBuyers] = useState(0);
  const [inquiriesInRange, setInquiriesInRange] = useState(0);
  const [selectionsInRange, setSelectionsInRange] = useState(0);
  const [sharesInRange, setSharesInRange] = useState(0);
  const [expiredLinks, setExpiredLinks] = useState(0);
  const [topProducts, setTopProducts] = useState<
    Array<{ productId: string; productName: string; score: number }>
  >([]);
  const [recentBuyers, setRecentBuyers] = useState<
    Array<{ name: string; lastAccess: string; sessions: number; phone?: string; linkToken?: string }>
  >([]);
  const [linkOpens, setLinkOpens] = useState<
    Array<{
      linkToken: string;
      name: string;
      opens: number;
      lastOpen: string;
      focused: boolean;
    }>
  >([]);

  useEffect(() => {
    const loadPerformance = async () => {
      if (!catalogue?.id) return;
      const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
      const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
      const start = activityRangeStart(timeRange);

      try {
        setLoading(true);
        setLoadError(null);

        const [viewsRes, linksRes, inquiriesRes, rollup] = await Promise.all([
          fetch(`${base}/catalogue-analytics/${catalogue.id}/views?limit=200&inviteesOnly=true`, {
            headers,
          }),
          fetch(`${base}/catalogue-security/${catalogue.id}/links`, { headers }),
          fetch(`${base}/catalogue-inquiry/catalogue/${encodeURIComponent(catalogue.id)}`, {
            headers,
          }),
          loadCatalogueCommerceRollup(catalogue.id),
        ]);

        const viewsJson = await viewsRes.json().catch(() => ({}));
        const linksJson = await linksRes.json().catch(() => ({}));
        const inquiriesJson = await inquiriesRes.json().catch(() => ({}));

        if (!viewsRes.ok && !linksRes.ok && !inquiriesRes.ok) {
          setLoadError("Could not load performance for this catalogue.");
          return;
        }

        const viewsList = Array.isArray(viewsJson?.data) ? viewsJson.data : [];
        const links = linksJson?.success && Array.isArray(linksJson.data) ? linksJson.data : [];
        const inquiries =
          inquiriesJson?.success && Array.isArray(inquiriesJson.data) ? inquiriesJson.data : [];
        const wishlists = Array.isArray(rollup.wishlists) ? rollup.wishlists : [];
        const carts = Array.isArray(rollup.carts) ? rollup.carts : [];

        setUniqueBuyers(uniqueBuyersInRange(viewsList, start));
        setInquiriesInRange(
          inquiries.filter((i: any) => inActivityRange(inquiryOccurredAt(i), start)).length
        );
        setSelectionsInRange(submittedSelectionCount(wishlists, carts, start));
        setSharesInRange(
          links.filter((l: any) => inActivityRange(l?.createdAt, start)).length
        );
        setExpiredLinks(expiredOrDisabledLinkCount(links, start));

        const products = Object.entries(rollup.byProductId)
          .map(([productId, sig]) => ({
            productId,
            productName: sig.productName || productId,
            score: submittedEngagementScore(sig),
            lastIso: sig.lastActivityIso,
          }))
          .filter((row) => row.score > 0 && inActivityRange(row.lastIso, start))
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
          .map(({ productId, productName, score }) => ({ productId, productName, score }));
        setTopProducts(products);

        const buyers = viewsList
          .filter((v: any) => isInviteeView(v))
          .filter((v: any) =>
            inActivityRange(v?.lastViewDate || v?.lastViewedAt || v?.firstViewDate, start)
          )
          .slice(0, 8)
          .map((view: any) => {
            const last = view?.lastViewDate || view?.lastViewedAt;
            const phone = String(view?.recipientPhone || "").replace(/\D/g, "");
            const displayPhone = phone.length > 4 ? `Buyer ···${phone.slice(-4)}` : "Buyer";
            return {
              name: String(view?.recipientName || view?.userName || "").trim() || displayPhone,
              lastAccess: last ? new Date(last).toLocaleString("en-IN") : "N/A",
              sessions: Number(view?.viewCount || 1),
              phone: phone || undefined,
              linkToken: String(view?.linkToken || "").trim() || undefined,
            };
          });
        setRecentBuyers(buyers);

        const focus = String(focusLinkToken || "").trim();
        const byToken = new Map<
          string,
          { name: string; opens: number; lastMs: number }
        >();
        viewsList
          .filter((v: any) => isInviteeView(v))
          .forEach((view: any) => {
            const token = String(view?.linkToken || "").trim();
            if (!token) return;
            const lastIso = view?.lastViewDate || view?.lastViewedAt || view?.firstViewDate;
            if (!inActivityRange(lastIso, start)) return;
            const prev = byToken.get(token) || { name: "", opens: 0, lastMs: 0 };
            const lastMs = lastIso ? new Date(lastIso).getTime() : 0;
            const name =
              String(view?.recipientName || view?.userName || "").trim() || prev.name;
            byToken.set(token, {
              name,
              opens: prev.opens + Number(view?.viewCount || 1),
              lastMs: Math.max(prev.lastMs, Number.isFinite(lastMs) ? lastMs : 0),
            });
          });
        links.forEach((link: any) => {
          const token = String(link?.linkToken || "").trim();
          if (!token || byToken.has(token)) return;
          byToken.set(token, {
            name: String(link?.recipientName || "").trim(),
            opens: 0,
            lastMs: 0,
          });
        });
        const rows = Array.from(byToken.entries())
          .map(([token, row]) => ({
            linkToken: token,
            name: row.name || "Invitee",
            opens: row.opens,
            lastOpen: row.lastMs ? new Date(row.lastMs).toLocaleString("en-IN") : "—",
            focused: Boolean(focus) && token === focus,
          }))
          .sort((a, b) => {
            if (a.focused !== b.focused) return a.focused ? -1 : 1;
            return b.opens - a.opens;
          });
        setLinkOpens(rows);
      } catch {
        setLoadError("Could not load performance for this catalogue.");
      } finally {
        setLoading(false);
      }
    };

    loadPerformance();
  }, [catalogue?.id, timeRange, focusLinkToken]);

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    footnote?: string;
  }> = ({ title, value, footnote }) => (
    <Card className="h-100 catalogue-panel">
      <CardBody className="text-center">
        <div className="text-muted small mb-1">{title}</div>
        <div className="h3 fw-bold mb-2">{value}</div>
        {footnote ? (
          <small className="text-muted d-block" style={{ fontSize: "11px" }}>
            {footnote}
          </small>
        ) : null}
      </CardBody>
    </Card>
  );

  const rangeLabel = timeRange === "7d" ? "Last 7 days" : timeRange === "90d" ? "Last 90 days" : "Last 30 days";

  const openRate =
    sharesInRange > 0 ? Math.min(100, (uniqueBuyers / sharesInRange) * 100) : null;
  const inquiryRate =
    uniqueBuyers > 0 ? Math.min(100, (inquiriesInRange / uniqueBuyers) * 100) : null;
  const fmtPct = (n: number | null) => (n != null ? `${n.toFixed(1)}%` : "—");

  const summaryLine = useMemo(
    () =>
      "Counts are invitees only (OTP-verified share links). Studio preview is excluded. Time on page is not tracked.",
    []
  );

  return (
    <div>
      <div className="catalogue-tab-toolbar">
        <h3 className="catalogue-tab-heading mb-0">Performance</h3>
        <select
          className="form-select"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>
      <p className="text-muted small mb-4">{summaryLine}</p>
      {loading && <p className="text-muted">Loading analytics…</p>}
      {loadError && <p className="text-danger small">{loadError}</p>}

      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Unique buyers"
            value={uniqueBuyers}
            footnote={`Invitees with a last open in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Inquiries"
            value={inquiriesInRange}
            footnote={`Messages and extension requests in ${rangeLabel.toLowerCase()}`}
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Submitted selections"
            value={selectionsInRange}
            footnote="Shortlists and carts sent from My selections"
          />
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <MetricCard
            title="Share links issued"
            value={sharesInRange}
            footnote={`${expiredLinks} expired or disabled in this range`}
          />
        </Col>
      </Row>

      <Row>
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <div className="px-3 py-3 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
              <h6 className="catalogue-panel-title">Top pieces by submitted selections</h6>
            </div>
            <CardBody>
              {topProducts.length === 0 && (
                <div className="text-muted small">
                  No submitted shortlist or cart lines in this range. Live shelf drafts stay on
                  Customer Inquiries until the visitor submits.
                </div>
              )}
              {topProducts.map((product, index) => (
                <div key={product.productId} className="d-flex align-items-center justify-content-between py-2">
                  <div className="d-flex align-items-center">
                    <span
                      className="badge me-3"
                      style={{
                        backgroundColor:
                          index === 0 ? "#ffd700" : index === 1 ? "#c0c0c0" : index === 2 ? "#cd7f32" : "#6c757d",
                        color: index < 3 ? "black" : "white",
                        minWidth: "24px",
                      }}
                    >
                      {index + 1}
                    </span>
                    <div>
                      <div className="fw-medium small">{product.productName}</div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {product.productId}
                      </div>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{product.score}</div>
                    <div className="text-muted small">score</div>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <div className="px-3 py-3 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
              <h6 className="catalogue-panel-title">Recent buyers</h6>
            </div>
            <CardBody>
              {recentBuyers.length === 0 && (
                <div className="text-muted small">
                  No invitee opens in this range yet. Studio preview does not count.
                </div>
              )}
              {recentBuyers.map((client, index) => (
                <div
                  key={`${client.name}-${client.linkToken || index}`}
                  className="d-flex align-items-center justify-content-between py-2"
                  style={
                    focusLinkToken && client.linkToken === focusLinkToken
                      ? { backgroundColor: "#eef6ff", borderRadius: 6, padding: "8px" }
                      : undefined
                  }
                >
                  <div>
                    <div className="fw-medium small">{client.name}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      Last open: {client.lastAccess}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{client.sessions}</div>
                    <div className="text-muted small">sessions</div>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-4">
          <Card>
            <div className="px-3 py-3 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
              <h6 className="catalogue-panel-title">Opens by share link</h6>
            </div>
            <CardBody>
              {linkOpens.length === 0 && (
                <div className="text-muted small">
                  No share links or invitee opens in this range yet.
                </div>
              )}
              {linkOpens.map((row) => (
                <div
                  key={row.linkToken}
                  className="d-flex align-items-center justify-content-between py-2"
                  style={
                    row.focused
                      ? { backgroundColor: "#eef6ff", borderRadius: 6, padding: "8px" }
                      : undefined
                  }
                >
                  <div>
                    <div className="fw-medium small">
                      {row.name}
                      {row.focused ? (
                        <span className="badge bg-primary ms-2">This open</span>
                      ) : null}
                    </div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      Last open: {row.lastOpen}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{row.opens}</div>
                    <div className="text-muted small">opens</div>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <div
              className="px-3 py-3 border-bottom d-flex align-items-center justify-content-between"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <h6 className="catalogue-panel-title">Derived rates</h6>
              <span className="small text-muted">{rangeLabel} · invitees only</span>
            </div>
            <CardBody>
              <Row>
                <Col md={4} className="mb-3">
                  <div className="text-center p-3 bg-light rounded h-100">
                    <div className="fw-bold text-primary h4">{fmtPct(openRate)}</div>
                    <div className="small text-muted">Open rate</div>
                    <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                      Unique buyers ÷ share links issued in this range (capped at 100%).
                    </div>
                  </div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="text-center p-3 bg-light rounded h-100">
                    <div className="fw-bold text-info h4">{fmtPct(inquiryRate)}</div>
                    <div className="small text-muted">Inquiry rate</div>
                    <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                      Inquiries ÷ unique buyers in this range.
                    </div>
                  </div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="text-center p-3 bg-light rounded h-100">
                    <div className="fw-bold text-secondary h4">{selectionsInRange}</div>
                    <div className="small text-muted">Submitted selections</div>
                    <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                      Average time spent is not tracked.
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PerformanceTab;
